import { type CardMapType } from '@/defs/card'
import {
  type CardFilterColorsOperatorType,
  type CardFilterNumberType,
  type CardFilterSearchType,
  type CardFilterSelectType,
  type CardFilterType,
} from '@/defs/cardFilters'
import { type SetOperatorsType } from '@/defs/common'
import { type PriceSourceType } from '@/defs/price'
import { type RecType } from '@/defs/rec'
import { applyOperator, hasSameValues, searchIncludes, toArray } from '@/utils/commonUtil'

type CardFilterBySlug = {
  [F in CardFilterType as F['slug']]: F
}

type ConfigType = {
  [S in keyof CardFilterBySlug]: (rec: RecType, cardFilter: CardFilterBySlug[S]) => boolean
}

export type Props = {
  cardFilters?: CardFilterType[]
  cardMap: CardMapType
  priceSource: PriceSourceType
  recs: RecType[]
}

type CompareCountProps = {
  cardFilter: CardFilterNumberType
  cardValue?: number
  partnerValue?: number
}

const compareCount = ({ cardFilter: { number, operator }, cardValue, partnerValue }: CompareCountProps) =>
  cardValue !== undefined &&
  (applyOperator(operator, cardValue, number) || !!(partnerValue && applyOperator(operator, partnerValue, number)))

type CompareSearchProps = {
  cardFilter: CardFilterSearchType
  cardValue?: string
  partnerValue?: string
}

const compareSearch = ({ cardFilter: { search }, cardValue = '', partnerValue = '' }: CompareSearchProps) =>
  searchIncludes(search, cardValue) || !!(partnerValue && searchIncludes(search, partnerValue))

type CompareSelectProps = {
  cardFilter: CardFilterSelectType
  cardValue?: string | string[]
  partnerValue?: string | string[]
}

const compareSelect = ({
  cardFilter: { operator, selections },
  cardValue = [],
  partnerValue = [],
}: CompareSelectProps) => {
  const selectionsCurrent = new Set([...toArray(cardValue), ...toArray(partnerValue)])
  const hasAny = selections.some(({ value }) => selectionsCurrent.has(value))
  const CONFIG: Record<SetOperatorsType, () => boolean> = {
    exclude: () => !hasAny,
    include: () => hasAny,
  }
  return CONFIG[operator]()
}

export const getFilteredGallery = ({ cardFilters, cardMap, priceSource, recs }: Props) => {
  const CONFIG: ConfigType = {
    cardName: ({ cardId, partnerRef }, cardFilter) =>
      compareSearch({
        cardFilter,
        cardValue: cardMap[cardId]?.name,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.name : undefined,
      }),
    cmc: ({ cardId, partnerRef }, cardFilter) =>
      compareCount({
        cardFilter,
        cardValue: cardMap[cardId]?.cmc || 0,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.cmc : undefined,
      }),
    colors: ({ cardId, partnerRef }, { colors, operator }) => {
      const colorsCards = new Set([
        ...(cardMap[cardId]?.colors || []),
        ...((partnerRef && cardMap[partnerRef.cardId]?.colors) || []),
      ])
      const CONFIG: Record<CardFilterColorsOperatorType, () => boolean> = {
        exclusiveAll: () => hasSameValues([...colorsCards], colors),
        inclusiveAll: () => colors.every((c) => colorsCards.has(c)),
        none: () => colors.every((c) => !colorsCards.has(c)),
        some: () => [...colorsCards].every((c) => colors.includes(c)),
      }
      return CONFIG[operator]()
    },
    decks: ({ numDecks }, cardFilter) => compareCount({ cardFilter, cardValue: numDecks }),
    inclusion: ({ inclusion }, cardFilter) => compareCount({ cardFilter, cardValue: (inclusion || 0) * 100 }),
    lift: ({ lift }, cardFilter) => compareCount({ cardFilter, cardValue: lift }),
    power: ({ cardId, partnerRef }, cardFilter) =>
      compareCount({
        cardFilter,
        cardValue: cardMap[cardId]?.power,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.power : undefined,
      }),
    price: ({ cardId, partnerRef }, cardFilter) =>
      compareCount({
        cardFilter,
        cardValue: cardMap[cardId]?.priceSources?.[priceSource]?.price,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.priceSources?.[priceSource]?.price : undefined,
      }),
    rarity: ({ cardId, partnerRef }, cardFilter) =>
      compareSelect({
        cardFilter,
        cardValue: cardMap[cardId]?.rarity,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.rarity : undefined,
      }),
    salt: ({ cardId, partnerRef }, cardFilter) =>
      compareCount({
        cardFilter,
        cardValue: cardMap[cardId]?.salt,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.salt : undefined,
      }),
    sets: ({ cardId, partnerRef }, cardFilter) =>
      compareSelect({
        cardFilter,
        cardValue: cardMap[cardId]?.sets,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.sets : undefined,
      }),
    synergy: ({ synergy }, cardFilter) => compareCount({ cardFilter, cardValue: synergy }),
    tags: ({ cardId, partnerRef }, cardFilter) =>
      compareSelect({
        cardFilter,
        cardValue: cardMap[cardId]?.tags,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.tags : undefined,
      }),
    toughness: ({ cardId, partnerRef }, cardFilter) =>
      compareCount({
        cardFilter,
        cardValue: cardMap[cardId]?.toughness,
        partnerValue: partnerRef ? cardMap[partnerRef.cardId]?.toughness : undefined,
      }),
    typeLine: ({ cardId, partnerRef }, cardFilter) =>
      compareSearch({
        cardFilter,
        cardValue: cardMap[cardId]?.type,
        partnerValue: partnerRef && cardMap[partnerRef.cardId]?.type,
      }),
    universesBeyond: ({ cardId }, cardFilter) => cardMap[cardId]?.isUniversesBeyond !== cardFilter.checked,
  }

  const applyCardFilter = <S extends keyof CardFilterBySlug>(
    rec: RecType,
    cardFilter: CardFilterBySlug[S] & { slug: S },
  ) => CONFIG[cardFilter.slug](rec, cardFilter)

  const recsFilters = !cardFilters
    ? [...recs]
    : recs.filter((rec) => cardFilters.every((cardFilter) => applyCardFilter(rec, cardFilter)))

  return {
    filteredCount: recsFilters.length,
    recs: recsFilters,
    recsCount: recs.length,
  }
}
